import React from 'react';
import styled from 'styled-components';
import Humanize from 'humanize-plus';
import { Flex } from '../../Flex';
import { Tooltip } from '../../Tooltip';
import { font, ChartCell } from './sharedStyles';
import { getColumnBorderFlags } from './columnBorderFlags';

const BarWrapper = styled.div<{ $showtext?: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  padding-top: 5px;
  padding-left: 4px;
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: flex-start;
  ${font}
  color: var(--table-cellrenderer-chart-wrapper-text-color);
  ${({ $showtext }) => $showtext === 1 && 'flex-direction: column-reverse;'}
`;

const PosNegBarCellWrapper = styled(Flex)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 11px !important;
  padding-right: 0;
  ${font}
`;
const PosNegBar = styled('div')<{ $width: number; $color: string }>`
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
  background: ${({ $color }) => $color};
  width: ${({ $width }) => $width}%;
  max-width: 50%;
  min-height: 14px;
`;
const PosNegBarMinus = styled('div')<{ $width: number; $color: string }>`
  border-top-left-radius: 30px;
  border-bottom-left-radius: 30px;
  background: ${({ $color }) => $color};
  width: ${({ $width }) => $width}%;
  max-width: 50%;
  min-height: 14px;
`;
const PosNegFakeBar = styled('div')`
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
  background: transparent;
  width: 50%;
  min-width: 50%;
  min-height: 14px;
`;
const BarWrapperMinus = styled(BarWrapper)`
  justify-content: flex-end;
  ${({ $showtext }) => $showtext === 1 && 'align-items: flex-end;'}
`;

export const PosNegBarChartRenderer =
  (
    dataColors: any,
    firstColumnIds: Set<string>,
    lastColumnIds: Set<string>,
    columnIds: string[],
    hasScroll: boolean | undefined,
  ) =>
  // eslint-disable-next-line react/display-name
  ({ dataKey, rowData, cellData, columnIndex }: any) => {
    const scoreMax = rowData[`${dataKey}Max`];
    const names = ['Negative', 'Positive'];
    const width = 50;
    const columnIdx = typeof columnIndex === 'number' ? columnIndex : -1;
    const colorsCandidate =
      Array.isArray(dataColors) && Array.isArray((dataColors as any)[columnIdx])
        ? (dataColors as any)[columnIdx]
        : dataColors;
    const colors = colorsCandidate || dataColors;
    const seriesData = [
      {
        data: [Number(cellData)],
        name: cellData < 0 ? names[0] : names[1],
        color: cellData < 0 ? colors[0] : colors[1],
      },
    ];
    const { isFirst, isLast } = getColumnBorderFlags(columnIds, columnIndex, firstColumnIds, lastColumnIds, hasScroll);

    return (
      <ChartCell $isfirst={isFirst ? 1 : 0} $islast={isLast ? 1 : 0}>
        <PosNegBarCellWrapper>
          {seriesData[0].data[0] < 0 ? (
            <BarWrapperMinus $showtext={0}>
              <Tooltip
                arrow
                placement="top"
                html={`${Humanize.toFixed(seriesData[0].data[0], 4).replace('.0000', '')}%`}
              >
                <PosNegBarMinus
                  $width={(width / scoreMax) * Math.abs(seriesData[0].data[0])}
                  $color={colors ? colors[0] : dataColors[0]}
                />
              </Tooltip>
              <PosNegFakeBar />
            </BarWrapperMinus>
          ) : (
            <BarWrapper $showtext={0}>
              <PosNegFakeBar />
              <Tooltip
                arrow
                placement="top"
                html={`${Humanize.toFixed(seriesData[0].data[0], 4).replace('.0000', '')}%`}
              >
                <PosNegBar
                  $width={(width / scoreMax) * seriesData[0].data[0]}
                  $color={colors ? colors[1] : dataColors[1]}
                />
              </Tooltip>
            </BarWrapper>
          )}
        </PosNegBarCellWrapper>
      </ChartCell>
    );
  };
